import { useMemo } from 'react';
import { useAuthProvider, useLogoutIfAccessDenied, usePermissions as originalUsePermissions } from 'react-admin';
import { UseQueryOptions } from 'react-query';
import { useSSRQuery } from './useSSRQuery';

const emptyParams = {};

/**
 * Hook for getting user permissions
 *
 * Calls the authProvider.getPermissions() method using react-query.
 * If the authProvider returns a rejected promise, returns empty permissions.
 *
 * The return value updates according to the request state:
 *
 * - start: { isLoading: true }
 * - success: { permissions: [any], isLoading: false }
 * - error: { error: [error from provider], isLoading: false }
 *
 * Useful to enable features based on user permissions
 *
 * @param {Object} params Any params you want to pass to the authProvider
 *
 * @returns The current auth check state. Destructure as { permissions, error, isLoading, refetch }.
 *
 * @example
 *     import { usePermissions } from 'react-admin';
 *
 *     const PostDetail = props => {
 *         const { isLoading, permissions } = usePermissions();
 *         if (!isLoading && permissions == 'editor') {
 *             return <PostEdit {...props} />
 *         } else {
 *             return <PostShow {...props} />
 *         }
 *     };
 */
export const usePermissions = <Permissions = any, Error = any>(
    params = emptyParams,
    queryParams: UseQueryOptions<Permissions, Error> = {
        staleTime: 5 * 60 * 1000,
    },
) => {
    if (!process.env.SERVER) {
        return originalUsePermissions(params, queryParams);
    }

    const authProvider = useAuthProvider();
    const logoutIfAccessDenied = useLogoutIfAccessDenied();

    const result = useSSRQuery(
        ['auth', 'getPermissions', params],
        authProvider ? () => authProvider.getPermissions(params) : async () => [],
        {
            onError: (error) => {
                if (process.env.NODE_ENV !== 'production') {
                    console.error(error);
                }
                logoutIfAccessDenied(error);
            },
            ...queryParams,
        },
    );

    return useMemo(
        () => ({
            permissions: result.data,
            isLoading: result.isLoading,
            error: result.error,
            refetch: result.refetch,
        }),
        [result],
    );
};
